/**
 * Single source of truth for void reasons.
 *
 * Used by both the admin-PIN void modal and the void log filter so the
 * dropdown values always match what gets persisted in `void_log.reason`.
 */
export const VOID_REASONS = [
  'Customer Request',
  'Kitchen Error',
  'Wrong Order',
  'Manager Decision',
  'Quality Issue',
  'Other',
] as const

export type VoidReason = (typeof VOID_REASONS)[number]

/** The one reason that carries no information on its own. */
export const OTHER_REASON: VoidReason = 'Other'

/**
 * The reasons offered as one-tap chips in the void modal.
 *
 * "Other" is deliberately absent: in the modal its place is taken by the
 * free-text field, so a server types what happened instead of picking a label
 * that says nothing. It stays in VOID_REASONS because the void-log filter must
 * still be able to select it — both for rows written before this field existed
 * and for the "Other: ..." rows written since.
 */
export const PRESET_REASONS = VOID_REASONS.filter(
  (r): r is Exclude<VoidReason, typeof OTHER_REASON> => r !== OTHER_REASON,
)

/** Free-text notes are capped so a stray paste can't bloat the audit row. */
export const VOID_NOTE_MAX = 120

/**
 * Build the string that gets persisted in `void_log.reason`.
 *
 * A bare "Other" tells whoever reads the void log nothing, which is the whole
 * point of the note. So "Other" is stored WITH its note, prefixed:
 *
 *     composeVoidReason('Other', 'guest spilled the tray')
 *       -> 'Other: guest spilled the tray'
 *
 * Keeping the category prefix (rather than storing the bare note) means the
 * void-log Reason filter still groups these rows under "Other" — the backend
 * matches `reason = 'Other'` OR `reason LIKE 'Other: %'`. `void_log` is
 * append-only, so this format is what future readers will see forever; don't
 * change the separator without a migration plan for existing rows.
 */
export function composeVoidReason(reason: VoidReason, note: string): string {
  if (reason !== OTHER_REASON) return reason
  const trimmed = note.trim()
  return trimmed ? `${OTHER_REASON}: ${trimmed}` : OTHER_REASON
}
